import { Card } from '../../../molecules/Card/Card';
import type { KPIMetric } from '../../../molecules/KPI/KPI';
import { KPIText } from '../../../molecules/KPIText/KPIText';
import { cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type MediaSummaryView = {
  /** The figure and its delta badge, over coverage rows only. */
  kpi: KPIMetric;
  /** Whether any story is in scope — the card is empty without one. */
  hasRows: boolean;
};

export type MediaSummaryCardProps = WidgetBlockProps & {
  view?: MediaSummaryView;
  title: string;
  helpText: string;
  helpSupportingText?: string;
};

/*
 * Media's three `Size=sm` figures — Figma `2123:83787`–`2123:83789`, added
 * 2026-09-21 as a row of three above the two charts (384 each on a 20px gap,
 * so `span={4}` of the twelve tracks).
 *
 * They are Overview's `MetricSummaryCard` in the prototype, not new components:
 * one metric, one figure, one delta badge, the header as the label. What makes
 * them Media's is the row set — **coverage only**, cut exactly as the two charts
 * cut it, so "Total mentions" on this page is the number of stories the bar
 * chart below sums to rather than a second, larger count of all social chatter.
 * The comparison figure is cut the same way. (Ported as a Media block of its own
 * so this group does not depend on the Overview group's blocks.)
 */
export function MediaSummaryCard({
  status,
  view,
  span = 4,
  chrome,
  onRetry,
  title,
  helpText,
  helpSupportingText,
}: MediaSummaryCardProps) {
  const hasData = view !== undefined && view.hasRows;
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      size="sm"
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={helpText}
      helpSupportingText={helpSupportingText}
      kpi={
        view && (
          <KPIText tone={view.kpi.tone} badge={view.kpi.badge}>
            {view.kpi.value}
          </KPIText>
        )
      }
    />
  );
}
